<template>
  <div
    ref="containerRef"
    class="tw-relative tw-overflow-hidden"
    @mouseenter="isHoverCard = true"
    @mouseleave="isHoverCard = false"
  >
    <template v-if="shouldLoadImage">
      <QImg
        v-if="ratio"
        class="tw-min-w-full tw-min-h-full"
        :src-set="srcset"
        :src="props.src"
        :class="customClass"
        :alt="alt"
        :title="alt"
        :sizes="sizes"
        :width="width"
        :height="height"
        :ratio="ratio"
        :fit="fit"
        :position="position"
        :placeholder-src="props.placeholderSrc"
        spinner-size="0"
        :loading="imageLoadingMode"
      />
      <QImg
        v-else
        :src-set="srcset"
        :src="props.src"
        :class="customClass"
        :alt="alt"
        :title="alt"
        :sizes="sizes"
        :width="width"
        :height="height"
        :fit="fit"
        :position="position"
        :placeholder-src="props.placeholderSrc"
        spinner-size="0"
        :loading="imageLoadingMode"
      />
    </template>
    <div
      v-else
      :style="placeholderStyle"
    />
    <div
      v-if="isShowIconPlay"
      class="tw-absolute tw-top-1/2 tw-left-1/2 -tw-translate-x-1/2 -tw-translate-y-1/2 !tw-p-0"
    >
      <QIcon
        :class="[
          'fa-brands fa-youtube tw-text-white',
          isMainCard ? 'tw-text-7xl' : 'tw-text-5xl',
          isHoverCard ? 'tw-opacity-100' : 'tw-opacity-70'
        ]"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onBeforeUnmount } from 'vue'

const props = withDefaults(defineProps<{
  srcset?: string
  src: string
  placeholderSrc?: string
  customClass?: string
  alt?: string
  sizes?: string
  width?: string
  height?: string
  ratio?: number
  fit?: 'cover' | 'fill' | 'contain' | 'none' | 'scale-down'
  isShowIconPlay?: boolean
  isMainCard?: boolean
  seoOptimize?: boolean
  position?: string
}>(), {
  srcset: '',
  placeholderSrc: undefined,
  customClass: '',
  alt: '',
  sizes: '',
  fit: 'cover' as const,
  width: '100%',
  height: '100%',
  ratio: undefined,
  isShowIconPlay: false,
  isMainCard: false,
  seoOptimize: false,
  position: '50% 50%',
})

const isHoverCard = ref(false)
const containerRef = ref<HTMLElement | null>(null)

// false during SSR and client hydration (both match → no hydration mismatch)
// set to true in onMounted so IntersectionObserver takes over
const isClient = ref(false)
const isIntersected = ref(false)

// During SSR + hydration: render real QImg (isClient=false → !false=true)
// After mount: only render QImg when intersected
const shouldLoadImage = computed(() => !isClient.value || isIntersected.value)

const placeholderStyle = computed(() => {
  if (props.ratio) {
    return { width: props.width, paddingTop: `${(1 / props.ratio) * 100}%` }
  }
  return { width: props.width, height: props.height }
})

let observer: IntersectionObserver | null = null
const preloadDistance = 1000
const preloadRootMargin = `${preloadDistance}px 0px`
const imageLoadingMode = computed(() => (isClient.value ? 'eager' : 'lazy'))

onMounted(() => {
  isClient.value = true

  if (!containerRef.value) {
    isIntersected.value = true
    return
  }

  // Already in viewport — load immediately
  const rect = containerRef.value.getBoundingClientRect()
  if (rect.top < window.innerHeight + preloadDistance) {
    isIntersected.value = true
    return
  }

  observer = new IntersectionObserver(
    ([entry]) => {
      if (entry.isIntersecting) {
        isIntersected.value = true
        observer?.disconnect()
        observer = null
      }
    },
    { rootMargin: preloadRootMargin },
  )
  observer.observe(containerRef.value)
})

onBeforeUnmount(() => {
  observer?.disconnect()
  observer = null
})
</script>
